import type { DataPoint } from '@/api/slcp/types';

/** A TABLE data point with no column definitions has nothing to render. */
function isRenderableTable(dataPoint: DataPoint): boolean {
  if (dataPoint.fieldTypeId !== 'TABLE') return true;
  const meta = dataPoint.meta as { columns?: unknown[] } | null | undefined;
  if (!meta) return false;
  return (meta.columns?.length ?? 0) > 0;
}

/**
 * The data points a profile template actually shows.
 *
 * Skips what cannot be rendered:
 *
 *  - no `fieldTypeId` — nothing to render it with
 *  - a TABLE whose meta carries no columns
 *
 * `country`, `city`, `zip_code` and the two address lines used to be skipped
 * here as well, because the v1 admin profile pulled them into an `addressInfo`
 * object and never listed them as rows. That was wrong for this renderer:
 * `ProfileSection` groups those same keys into `AddressMapBlock`, so removing
 * them did not tidy the field list — it emptied the address popover. `geojson`
 * is not one of them and survived, which left the block rendering its map with
 * no fields above it (BI-1845).
 *
 * They stay in now, which also matches what the customer profile at
 * `/network/:id/general-info` shows, since both screens share this renderer.
 */
export function visibleProfileDataPoints(
  data: Record<string, DataPoint> | undefined,
): Record<string, DataPoint> {
  if (!data) return {};
  return Object.fromEntries(
    Object.entries(data).filter(([, dataPoint]) => {
      if (!dataPoint?.fieldTypeId) return false;
      if (dataPoint.isVisible === false) return false;
      return isRenderableTable(dataPoint);
    }),
  );
}
